/**
 * =====================================================================
 * IMPORTANT - READ BEFORE MODIFYING ANY LIST ENDPOINT IN THIS FILE:
 * =====================================================================
 *
 * TNN's Django backend paginates ALL list endpoints by default:
 *
 *   API returns: { "count": N, "results": [...actual data...] }
 *   baseApi unwraps to: { "count": N, "results": [...] }  (NOT an array)
 *
 * Every ``build.query<Type[], ...>`` endpoint MUST have:
 *
 *   transformResponse: (r: { results?: Type[] }) =>
 *     Array.isArray(r?.results) ? r.results : [],
 *
 * Without this, ``.map()`` on the paginated object either crashes
 * ("TypeError: x.map is not a function") or silently shows nothing.
 * This was the #1 bug class in the v1.6 and v4.0.1 QA reports.
 * =====================================================================
 */
/**
 * Copilot API endpoints.
 *
 * All routes require authenticated tenant user; tenant scoping is automatic
 * via the X-Tenant-Slug header set in baseApi.
 *
 * Tag types:
 *   - "Conversation" : per-conversation detail cache, invalidated on send
 *   - "ConversationList" : list cache, invalidated on create/archive
 *   - "Playbook" : per-playbook + list cache
 */
import { baseApi } from "@/app/store/api/baseApi";

import type {
  ConversationDetail,
  ConversationListItem,
  CopilotTool,
  PlaybookDetail,
  PlaybookDraft,
  PlaybookDryRunResult,
  PlaybookListItem,
} from "../model/types";

/** RTK Query endpoints for the AI copilot (conversations, messages, playbooks). */
export const copilotApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // -------------------------------------------------------------------------
    // Conversations
    // -------------------------------------------------------------------------

    /** GET /api/v1/copilot/conversations/ */
    listConversations: build.query<
      { results: ConversationListItem[]; count: number },
      void
    >({
      query: () => "/copilot/conversations/",
      providesTags: (result) =>
        result
          ? [
              ...result.results.map((c) => ({
                type: "Conversation" as const,
                id: c.id,
              })),
              { type: "Conversation" as const, id: "LIST" },
            ]
          : [{ type: "Conversation" as const, id: "LIST" }],
    }),

    /** GET /api/v1/copilot/conversations/{id}/ */
    getConversation: build.query<ConversationDetail, { id: number }>({
      query: ({ id }) => `/copilot/conversations/${id}/`,
      providesTags: (_result, _error, { id }) => [
        { type: "Conversation", id },
      ],
    }),

    /** POST /api/v1/copilot/conversations/ */
    createConversation: build.mutation<
      ConversationDetail,
      { title?: string; playbook_slug?: string; inputs?: Record<string, unknown> }
    >({
      query: (body) => ({
        url: "/copilot/conversations/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Conversation", id: "LIST" }],
    }),

    /** POST /api/v1/copilot/conversations/{id}/message/ */
    sendMessage: build.mutation<
      ConversationDetail,
      { id: number; text: string; client_message_id?: string }
    >({
      query: ({ id, text, client_message_id }) => ({
        url: `/copilot/conversations/${id}/message/`,
        method: "POST",
        body: { text, client_message_id },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Conversation", id },
        { type: "Conversation", id: "LIST" },
      ],
    }),

    /** POST /api/v1/copilot/conversations/{id}/archive/ */
    archiveConversation: build.mutation<
      { id: number; status: string },
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/copilot/conversations/${id}/archive/`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Conversation", id },
        { type: "Conversation", id: "LIST" },
      ],
    }),

    /** POST /api/v1/copilot/conversations/{id}/edit-message/
     *  Rewords a sent message and re-runs from it. DESTRUCTIVE: the backend
     *  deletes that message and everything recorded after it first, because a
     *  transcript whose answers no longer match their question is a record
     *  that lies. Confirm before calling. */
    editMessage: build.mutation<
      ConversationDetail,
      { id: number; message_id: number; text: string }
    >({
      query: ({ id, message_id, text }) => ({
        url: `/copilot/conversations/${id}/edit-message/`,
        method: "POST",
        body: { message_id, text },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Conversation", id },
        { type: "Conversation", id: "LIST" },
      ],
    }),

    /** PATCH /api/v1/copilot/conversations/{id}/rename/
     *  Titles are written by the system, so a list of playbook runs is a list
     *  of identical names. The owner renames their own. */
    renameConversation: build.mutation<
      { id: number; title: string },
      { id: number; title: string }
    >({
      query: ({ id, title }) => ({
        url: `/copilot/conversations/${id}/rename/`,
        method: "PATCH",
        body: { title },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Conversation", id },
        { type: "Conversation", id: "LIST" },
      ],
    }),

    /** POST /api/v1/copilot/conversations/{id}/flag/
     *  v3.8 bug report v1.6 Finding 13: let users flag a conversation
     *  for admin review when the copilot hits a snag. */
    flagConversation: build.mutation<
      { flagged: boolean; conversation_id: number; note: string },
      { id: number; note?: string }
    >({
      query: ({ id, note }) => ({
        url: `/copilot/conversations/${id}/flag/`,
        method: "POST",
        body: { note: note ?? "" },
      }),
    }),

    // -------------------------------------------------------------------------
    // Playbooks
    // -------------------------------------------------------------------------

    /** GET /api/v1/copilot/playbooks/ */
    listPlaybooks: build.query<
      { results: PlaybookListItem[]; count: number },
      { page?: number; page_size?: number } | void
    >({
      query: (params) => {
        const page = params?.page ?? 1;
        const page_size = params?.page_size ?? 100;
        const search = new URLSearchParams({
          page: String(page),
          page_size: String(page_size),
        });
        return `/copilot/playbooks/?${search.toString()}`;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map((p) => ({
                type: "Playbook" as const,
                id: p.id,
              })),
              { type: "Playbook" as const, id: "LIST" },
            ]
          : [{ type: "Playbook" as const, id: "LIST" }],
    }),

    /** GET /api/v1/copilot/playbooks/{id}/ */
    getPlaybook: build.query<PlaybookDetail, { id: number }>({
      query: ({ id }) => `/copilot/playbooks/${id}/`,
      providesTags: (_result, _error, { id }) => [{ type: "Playbook", id }],
    }),

    /** GET /api/v1/copilot/playbooks/tools/ - every tool, for the builder. */
    listCopilotTools: build.query<{ tools: CopilotTool[]; count: number }, void>({
      query: () => "/copilot/playbooks/tools/",
    }),

    /** POST /api/v1/copilot/playbooks/dry-run/ - test a draft in the sandbox. */
    dryRunPlaybook: build.mutation<
      PlaybookDryRunResult,
      {
        system_prompt: string;
        allowed_tools: string[];
        sample_message: string;
      }
    >({
      query: (body) => ({
        url: "/copilot/playbooks/dry-run/",
        method: "POST",
        body,
      }),
    }),

    /** POST /api/v1/copilot/playbooks/ - create (OWNER/ADMIN only). */
    createPlaybook: build.mutation<PlaybookDetail, PlaybookDraft>({
      query: (body) => ({
        url: "/copilot/playbooks/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Playbook", id: "LIST" }],
    }),

    /** PATCH /api/v1/copilot/playbooks/{id}/ - edit (OWNER/ADMIN only). */
    updatePlaybook: build.mutation<
      PlaybookDetail,
      { id: number } & Partial<PlaybookDraft>
    >({
      query: ({ id, ...body }) => ({
        url: `/copilot/playbooks/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Playbook", id },
        { type: "Playbook", id: "LIST" },
      ],
    }),

    /** POST /api/v1/copilot/playbooks/{id}/clone/ - clone to an editable copy. */
    clonePlaybook: build.mutation<PlaybookDetail, { id: number }>({
      query: ({ id }) => ({
        url: `/copilot/playbooks/${id}/clone/`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "Playbook", id: "LIST" }],
    }),

    /** DELETE /api/v1/copilot/playbooks/{id}/ - soft-disable. */
    deletePlaybook: build.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/copilot/playbooks/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Playbook", id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListConversationsQuery,
  useGetConversationQuery,
  useCreateConversationMutation,
  useSendMessageMutation,
  useArchiveConversationMutation,
  useRenameConversationMutation,
  useEditMessageMutation,
  useFlagConversationMutation,
  useListPlaybooksQuery,
  useGetPlaybookQuery,
  useListCopilotToolsQuery,
  useDryRunPlaybookMutation,
  useCreatePlaybookMutation,
  useUpdatePlaybookMutation,
  useClonePlaybookMutation,
  useDeletePlaybookMutation,
} = copilotApi;
